import {
  ACCEPT_BUTTON_CLICKED,
  ACTIVE_PROVIDER_SEARCH_VERSIONS,
  APP_VERSION_NAMES,
  BACK_BUTTON_CLICKED,
  DECLINE_BUTTON_CLICKED,
  MESSENGER_TAB_MESSENGER,
  REJECT_BUTTON_CLICKED,
  SEND_REQUEST_BUTTON_CLICKED,
  TRACK_EVENTS,
  VALIDATE_BUTTON_CLICKED,
} from "core/consts";
import {
  ENV_DEMO,
  ENV_DEVELOPMENT,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
} from "core/model/config";
import * as z from "zod";
import { isTitleCase } from "../../model/utils/strings";
import {
  dateFormatRFC3339,
  generateTrackName,
  numberOrNull,
  pageNameSchema,
  stringOrNull,
} from "../utils";

/** * * * * BASE EVENT SCHEMA * * * * * * * * * * * * * * * * * * * * * * * *
 * The base event schema is the foundation for all events submitted to BI.
 * It contains the core properties that every event must have.
 * This schema serves as a baseline for all events and is used to validate
 * the basic structure of an event before it is processed further.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
export type BaseEventSchema = z.infer<typeof baseEventSchema>;
export type PlatformInfo = z.infer<typeof platform_information>;
export type TrackingEventName =
  | ReturnType<typeof generateTrackName>
  | z.infer<typeof trackingEventName>;

const trackingEventName = z.enum(TRACK_EVENTS);
const buttonOrLinkEventName = z.string().superRefine((value, ctx) => {
  const trimmedValue = value.trim();
  const validSuffixes = ["Button Clicked", "Link Clicked"];
  const matchingSuffix = validSuffixes.find((suffix) =>
    trimmedValue.endsWith(suffix),
  );

  if (!matchingSuffix) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: "string",
      message: `Invalid event name. Expected one of: ${validSuffixes.join(
        " or ",
      )}.`,
      path: ["id"],
      received: typeof trimmedValue,
    });
    return;
  }

  // Extract the base ID by removing the suffix and any extra spaces
  const suffixLength = matchingSuffix.length;
  const id = trimmedValue.slice(0, trimmedValue.length - suffixLength).trim();

  if (!id) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: "string",
      message: `Missing ID before suffix "${matchingSuffix}". Received "${trimmedValue}".`,
      path: ["id"],
      received: typeof trimmedValue,
    });
    return;
  }

  if (!isTitleCase(id)) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: "string",
      message: `Invalid casing. Expected Title Case for ID. Received ${trimmedValue}`,
      path: ["id"],
      received: typeof trimmedValue,
    });
  }

  const expectedEventName = `${id} ${matchingSuffix}`;
  if (trimmedValue !== expectedEventName) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: "string", // General format check
      message: `Invalid name format. Expected "${expectedEventName}". Received ${trimmedValue}`,
      path: ["id"],
      received: typeof trimmedValue,
    });
  }
});

const trackingEventNameExtended = z.custom<string>((value) => {
  const trackEventValues = Object.values(TRACK_EVENTS) as string[];
  if (typeof value === "string" && trackEventValues.includes(value)) {
    return trackingEventName.parse(value);
  } else {
    return buttonOrLinkEventName.parse(value);
  }
});

const env = z.union([
  z.literal(ENV_DEMO),
  z.literal(ENV_DEVELOPMENT),
  z.literal(ENV_PREPROD),
  z.literal(ENV_PRODUCTION),
  z.literal(ENV_STAGING),
  z.null(),
]);

const user_information = z.object({
  account_id: numberOrNull,
  auction_id: numberOrNull,
  auction_request_id: numberOrNull,
  careprovider_id: numberOrNull,
  careprovider_name: stringOrNull,
  careseeker_id: numberOrNull,
  careseeker_name: stringOrNull,
  consultation_id: numberOrNull,
  first_name: stringOrNull,
  last_name: stringOrNull,
  patient_id: numberOrNull,
});

/** For reads of in-memory tracking snapshots that may omit fields. */
export const trackingUserInformationPartialSchema = user_information.partial();

export type TrackingUserInformationPartial = z.infer<
  typeof trackingUserInformationPartialSchema
>;

const platform_information = z.object({
  application: z.enum(APP_VERSION_NAMES),
  browser: stringOrNull,
  browser_version: stringOrNull,
  build: stringOrNull,
  device_id: stringOrNull,
  device_screen_height: numberOrNull,
  device_screen_width: numberOrNull,
  env,
  hostname: stringOrNull,
  is_pwa: z.boolean(),
  model: stringOrNull,
  operation_system: stringOrNull,
  operation_system_version: stringOrNull,
  platform: stringOrNull,
  provider_search_state: z
    .nullable(z.enum(ACTIVE_PROVIDER_SEARCH_VERSIONS))
    .optional(),
  version: stringOrNull,
});

export const baseEventSchema = z.object({
  name: trackingEventNameExtended,
  page_information: z
    .object({
      duration_in_seconds: z.number().optional(),
      page_name: pageNameSchema,
      params: z.record(z.string(), z.string()).optional(),
      path: z.string(),
      pathname: z.string(),
      previous_page: z.string(),
      referrer: z.string(),
      total_duration_in_seconds: z.number().optional(),
    })
    .optional(),
  platform_information,
  session_information: z.object({
    session_id: z.string(),
    session_item: z.number(),
  }),
  system_event: z.boolean().optional(),
  timestamp: dateFormatRFC3339,
  user_information,
});

/** * * * BASE EVENT SCHEMA (Sender VARIANT) * * * * * * * * * * * * * * * * * *
 * A variation of the base event schema has been created specifically
 * for events generated by the sender app.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const baseEventSchemaSender = baseEventSchema
  .strict()
  .superRefine((data, ctx) => {
    if (data.user_information.careprovider_id !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: "null",
        message: "careprovider_id must be null in the careseeker app",
        path: ["user_information", "careprovider_id"],
        received: typeof data.user_information.careprovider_id,
      });
    }
  });

/** * * * BASE EVENT SCHEMA (Careprovider VARIANT) * * * * * * * * * * * * * * * * * *
 * A variation of the base event schema has been created specifically
 * for events generated by the careprovider app.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const baseEventSchemaCareprovider = baseEventSchema
  .strict()
  .superRefine((data, ctx) => {
    if (data.user_information.careseeker_id !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: "null",
        message: "careseeker_id must be null in the careprovider app",
        path: ["user_information", "careseeker_id"],
        received: typeof data.user_information.careseeker_id,
      });
    }
  });

/** * * * CUSTOM EVENT SCHEMAS * * * * * * * * * * * * * * * * * * * * * * *
 * In addition to the base event schema, custom event schemas can be defined for specific events.
 * These schemas are used for validation of events that require
 * specific properties in addition to the base properties.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const customBaseEventSchema = z.object({
  name: trackingEventNameExtended,
});

export type UserManagementUpdateAccountSchema = z.infer<
  typeof userManagementUpdateAccountSchema
>;
export const userManagementUpdateAccountSchema = customBaseEventSchema
  .extend({
    edited_account_id: z.number(),
    name: z.union([
      z.literal(TRACK_EVENTS.UM_DEACTIVATE_CLICKED),
      z.literal(TRACK_EVENTS.UM_DEACTIVATE_ERROR),
      z.literal(TRACK_EVENTS.UM_DEACTIVATE_MODAL_CANCELLED),
      z.literal(TRACK_EVENTS.UM_DEACTIVATE_MODAL_CONFIRMED),
      z.literal(TRACK_EVENTS.UM_DEACTIVATE_SUCCESS),
      z.literal(TRACK_EVENTS.UM_EDIT_CLICKED),
      z.literal(TRACK_EVENTS.UM_EDIT_ERROR),
      z.literal(TRACK_EVENTS.UM_EDIT_MODAL_CANCELLED),
      z.literal(TRACK_EVENTS.UM_EDIT_MODAL_CONFIRMED),
      z.literal(TRACK_EVENTS.UM_EDIT_SUCCESS),
      z.literal(TRACK_EVENTS.UM_REACTIVATE_CLICKED),
      z.literal(TRACK_EVENTS.UM_REACTIVATE_ERROR),
      z.literal(TRACK_EVENTS.UM_REACTIVATE_MODAL_CANCELLED),
      z.literal(TRACK_EVENTS.UM_REACTIVATE_MODAL_CONFIRMED),
      z.literal(TRACK_EVENTS.UM_REACTIVATE_SUCCESS),
      z.literal(TRACK_EVENTS.UM_RESEND_ACTIVATION_EMAIL_CLICKED),
      z.literal(TRACK_EVENTS.UM_RESEND_ACTIVATION_EMAIL_ERROR),
      z.literal(TRACK_EVENTS.UM_RESEND_ACTIVATION_EMAIL_MODAL_CANCELLED),
      z.literal(TRACK_EVENTS.UM_RESEND_ACTIVATION_EMAIL_MODAL_CONFIRMED),
      z.literal(TRACK_EVENTS.UM_RESEND_ACTIVATION_EMAIL_SUCCESS),
      z.literal(TRACK_EVENTS.UM_RESET_PASSWORD_EMAIL_CLICKED),
      z.literal(TRACK_EVENTS.UM_RESET_PASSWORD_EMAIL_ERROR),
      z.literal(TRACK_EVENTS.UM_RESET_PASSWORD_EMAIL_MODAL_CANCELLED),
      z.literal(TRACK_EVENTS.UM_RESET_PASSWORD_EMAIL_MODAL_CONFIRMED),
      z.literal(TRACK_EVENTS.UM_RESET_PASSWORD_EMAIL_SUCCESS),
    ]),
  })
  .strict();

export type PrintButtonConfirmedSchema = z.infer<
  typeof printButtonConfirmedSchema
>;
export const printButtonConfirmedSchema = customBaseEventSchema
  .extend({
    print_chat_history: z.boolean(),
    print_profile_information: z.boolean(),
  })
  .strict();

export type RequestActionButtonSchema = z.infer<
  typeof requestActionButtonSchema
>;
export const requestActionButtonSchema = z
  .object({
    action: z.union([
      z.literal(ACCEPT_BUTTON_CLICKED),
      z.literal(DECLINE_BUTTON_CLICKED),
      z.literal(REJECT_BUTTON_CLICKED),
      z.literal(SEND_REQUEST_BUTTON_CLICKED),
      z.literal(VALIDATE_BUTTON_CLICKED),
    ]),
    name: z.literal(TRACK_EVENTS.REQUEST_ACTION),
  })
  .strict();

export type RequestPanelSchema = z.infer<typeof requestPanelSchema>;
export const requestPanelSchema = z
  .object({
    action: z.union([
      z.literal(ACCEPT_BUTTON_CLICKED),
      z.literal(BACK_BUTTON_CLICKED),
      z.literal(DECLINE_BUTTON_CLICKED),
      z.literal(REJECT_BUTTON_CLICKED),
      z.literal(SEND_REQUEST_BUTTON_CLICKED),
      z.literal(VALIDATE_BUTTON_CLICKED),
    ]),
    name: z.union([
      z.literal(TRACK_EVENTS.REQUEST_PANEL_ACCEPT),
      z.literal(TRACK_EVENTS.REQUEST_PANEL_DECLINE),
      z.literal(TRACK_EVENTS.REQUEST_PANEL_REJECT),
      z.literal(TRACK_EVENTS.REQUEST_PANEL_SEND_REQUEST),
      z.literal(TRACK_EVENTS.REQUEST_PANEL_VALIDATE),
    ]),
  })
  .strict();

export const messengerSendMessageSchema = z.object({
  auction_request_status: z.number(),
  name: z.literal(TRACK_EVENTS.MESSENGER_SEND_MESSAGE_BUTTON),
  tab: z.literal(MESSENGER_TAB_MESSENGER),
});

export type EditPatientButtonSchema = z.infer<typeof editPatientButtonSchema>;

export const editPatientButtonSchema = z.object({
  auction_status: z.string(),
  name: z.literal(TRACK_EVENTS.EDIT_PATIENT_BUTTON_CLICKED),
});

export type ContactReceiverButtonSchema = z.infer<
  typeof contactReceiverButtonSchema
>;

export const contactReceiverButtonSchema = z.object({
  auction_status: z.string(),
  name: z.literal(TRACK_EVENTS.CONTACT_RECEIVER_BUTTON_CLICKED),
});

export type TableSortingButtonSchema = z.infer<typeof tableSortingButtonSchema>;

export const tableSortingButtonSchema = z.object({
  name: z.union([
    z.literal(TRACK_EVENTS.TABLE_SORT_BY_DEFAULT_CLICKED),
    z.literal(TRACK_EVENTS.TABLE_SORT_BY_DISTANCE_CLICKED),
    z.literal(TRACK_EVENTS.TABLE_SORT_BY_NAME_CLICKED),
  ]),
  table: z.string(),
});

export type SearchReceiversButtonSchema = z.infer<
  typeof searchReceiversButtonSchema
>;

export const searchReceiversButtonSchema = z.object({
  auction_status: z.string(),
  name: z.literal(TRACK_EVENTS.SEARCH_PROVIDER_BUTTON_CLICKED),
});

export const contactAnywayConfirmButtonSchema = z.object({
  careprovider_id: z.number(),
  name: z.literal(TRACK_EVENTS.CONTACT_ANYWAY_MODAL_CONFIRM_BUTTON_CLICKED),
});
